import {
  type AccountInfo,
  InteractionRequiredAuthError,
  type IPublicClientApplication,
} from "@azure/msal-browser";

import { apiScopes } from "../auth/authConfig";
import { getGroupOverride, getRoleOverride } from "../hooks/useRoleOverride";

// API base URL is injected at build time via VITE_API_URL.
// - K8S: workflow sets VITE_API_URL to the APIM path.
// - Local dev / Vitest: defaults to "/api" (Vite proxy catches /api → backend).
// VITE_API_URL MUST NOT end with "/" — request paths already start with "/".
const BASE_URL = import.meta.env.VITE_API_URL || "/api";
const TIMEOUT_MS = 30_000;
const UNAUTHENTICATED_SAFE_PATHS = new Set<string>(["/health"]);

let msalInstance: IPublicClientApplication | null = null;
let msalAccount: AccountInfo | null = null;

/** Deduplicates concurrent token refresh attempts. */
let tokenRefreshPromise: Promise<string> | null = null;

/** Called once from AppLayout to wire up MSAL for authenticated requests. */
export function setMsalContext(
  instance: IPublicClientApplication,
  account: AccountInfo,
) {
  msalInstance = instance;
  msalAccount = account;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  if (!msalInstance || !msalAccount) return {};
  try {
    const response = await msalInstance.acquireTokenSilent({
      ...apiScopes,
      account: msalAccount,
    });
    return { Authorization: `Bearer ${response.accessToken}` };
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({
        ...apiScopes,
        account: msalAccount,
      });
    } else {
      console.error("Token acquisition failed — redirecting to login:", error);
      await msalInstance.acquireTokenRedirect({
        ...apiScopes,
        account: msalAccount,
      });
    }
    return {};
  }
}

/**
 * Attempts a forced silent token refresh, deduplicating concurrent calls.
 * Returns the new access token, or throws if refresh fails.
 */
async function refreshAccessToken(): Promise<string> {
  if (!msalInstance || !msalAccount) {
    throw new Error("MSAL not configured");
  }
  if (tokenRefreshPromise) return tokenRefreshPromise;
  tokenRefreshPromise = msalInstance
    .acquireTokenSilent({
      ...apiScopes,
      account: msalAccount,
      forceRefresh: true,
    })
    .then((r) => r.accessToken)
    .finally(() => {
      tokenRefreshPromise = null;
    });
  return tokenRefreshPromise;
}

export class ApiError extends Error {
  status: number;
  statusText: string;
  body: unknown;

  constructor(status: number, statusText: string, body: unknown) {
    super(`API ${status}: ${statusText}`);
    this.name = "ApiError";
    this.status = status;
    this.statusText = statusText;
    this.body = body;
  }
}

export class AuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthError";
  }
}

function isUnauthenticatedSafePath(path: string): boolean {
  return UNAUTHENTICATED_SAFE_PATHS.has(path);
}

async function buildAuthHeadersForPath(
  path: string,
): Promise<Record<string, string>> {
  if (isUnauthenticatedSafePath(path)) {
    return {};
  }

  if (!msalInstance || !msalAccount) {
    throw new AuthError(
      `Authentication context is unavailable for protected API path: ${path}`,
    );
  }

  const authHeaders = await getAuthHeaders();
  if (!authHeaders.Authorization) {
    throw new AuthError(
      `Access token acquisition failed for protected API path: ${path}`,
    );
  }

  return authHeaders;
}

type QueryParamScalar = string | number | boolean;

async function request<T>(
  path: string,
  options: RequestInit & {
    params?: Record<
      string,
      QueryParamScalar | readonly QueryParamScalar[] | undefined
    >;
    timeout?: number;
  } = {},
): Promise<T> {
  const { params, timeout = TIMEOUT_MS, ...init } = options;

  let url = `${BASE_URL}${path}`;
  if (params) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined) continue;
      if (Array.isArray(value)) {
        for (const item of value) {
          searchParams.append(key, String(item));
        }
        continue;
      }

      searchParams.append(key, String(value));
    }

    const serializedParams = searchParams.toString();
    if (serializedParams.length > 0) {
      url += `?${serializedParams}`;
    }
  }

  const authHeaders = await buildAuthHeadersForPath(path);
  const roleOverride = getRoleOverride();
  const groupOverride = getGroupOverride();
  const timeoutController = new AbortController();
  const callerSignal = init.signal;
  const requestSignal = callerSignal
    ? AbortSignal.any([callerSignal, timeoutController.signal])
    : timeoutController.signal;
  const timer = setTimeout(() => timeoutController.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...init,
      signal: requestSignal,
      headers: {
        ...authHeaders,
        ...(roleOverride ? { "X-Role-Override": roleOverride } : {}),
        ...(roleOverride === "Gruppmedlem" && groupOverride
          ? { "X-Group-Override": String(groupOverride) }
          : {}),
        ...init.headers,
      },
    });

    if (!response.ok) {
      // 401 → attempt one silent token refresh and retry
      if (response.status === 401 && msalInstance && msalAccount) {
        try {
          const newToken = await refreshAccessToken();
          const retryResponse = await fetch(url, {
            ...init,
            signal: requestSignal,
            headers: {
              Authorization: `Bearer ${newToken}`,
              ...(roleOverride ? { "X-Role-Override": roleOverride } : {}),
              ...(roleOverride === "Gruppmedlem" && groupOverride
                ? { "X-Group-Override": String(groupOverride) }
                : {}),
              ...init.headers,
            },
          });
          if (retryResponse.ok) {
            const ct = retryResponse.headers.get("content-type");
            if (ct?.includes("application/json")) {
              return (await retryResponse.json()) as T;
            }
            return (await retryResponse.text()) as unknown as T;
          }
          // Still failing after refresh — redirect to login
          await msalInstance.acquireTokenRedirect({
            ...apiScopes,
            account: msalAccount,
          });
          // Page navigates away; this line is unreachable
          throw new ApiError(401, "Session expired", null);
        } catch (error) {
          if (error instanceof ApiError) throw error;
          // Silent refresh failed — redirect to login
          await msalInstance.acquireTokenRedirect({
            ...apiScopes,
            account: msalAccount,
          });
          throw new ApiError(401, "Session expired", null);
        }
      }

      const bodyText = await response.text();
      let body: unknown;
      try {
        body = JSON.parse(bodyText);
      } catch {
        body = bodyText;
      }
      throw new ApiError(response.status, response.statusText, body);
    }

    const contentType = response.headers.get("content-type");
    if (contentType?.includes("application/json")) {
      return (await response.json()) as T;
    }
    return (await response.text()) as unknown as T;
  } finally {
    clearTimeout(timer);
  }
}

export const apiClient = {
  get<T>(
    path: string,
    options?: {
      params?: Record<
        string,
        QueryParamScalar | readonly QueryParamScalar[] | undefined
      >;
    },
  ): Promise<T> {
    return request<T>(path, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      params: options?.params,
    });
  },

  post<T>(
    path: string,
    body?: unknown,
    options?: { headers?: Record<string, string>; signal?: AbortSignal },
  ): Promise<T> {
    const isFormData = body instanceof FormData;
    return request<T>(path, {
      method: "POST",
      signal: options?.signal,
      headers: isFormData
        ? (options?.headers ?? {})
        : { "Content-Type": "application/json", ...options?.headers },
      body: isFormData
        ? body
        : body !== undefined
          ? JSON.stringify(body)
          : undefined,
    });
  },

  patch<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  },

  put<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  },

  delete<T>(path: string): Promise<T> {
    return request<T>(path, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
    });
  },
};
